import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, Switch, FormControlLabel } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import InfoTooltip from '../../common_components/InfoTooltip';
import moment from "moment";
import { formatStat } from "../../helper/statsFormatter";
import CommonTable from "../../common_components/CommonTable";
import { NETWORK_ICON_MAP } from '../../utils/networkConstants';
import TableToolbar from "../../common_components/TableToolbar";
import HoverTooltip from "../../common_components/hoverOverlay/HoverTooltip";
import "../../common_components/daterangepicker/CustomDatePicker.css";
import { StyledCurrency } from "../../helper/StyledCurrency";
import { savePreferences, selectPreferences, updateNetworkPreference } from '../../redux/divergenceSlice';
import CommonLoader from "../../common_components/CommonLoader";
const formatMetricValue = (value, key) => {
    if (value === undefined || value === null)
        return '-';
    return formatStat(value, key);
};
const MetricCell = ({ current, compared, change, metricKey, isSummary, row }) => {
    const hasCompared = compared !== undefined && compared !== null;
    const isChangeValid = change !== undefined && !isNaN(change);
    const keyLower = String(metricKey).toLowerCase();
    const isCpocOrCpa = [
        'cpoc', 'cpa', 'cpc', 'meta_cpoc', 'google_cpoc', 'outbound_cpc', 'cost_per_session', 'cost_per_click', 'cost_per_order', 'cpm'
    ].includes(keyLower)
        || keyLower.includes('cpoc')
        || keyLower.includes('cpa')
        || keyLower.includes('cpc')
        || keyLower.includes('cost_per_')
        || keyLower.includes('cpm');
    const isSpendOrCost = !isCpocOrCpa && (['spend', 'cost', 'meta_cost', 'google_cost', 'total_cost'].includes(keyLower)
        || keyLower.includes('spend')
        || keyLower.includes('cost'));
    let isRoasDown = false;
    if (row && isSpendOrCost) {
        const roasKey = Object.keys(row.changes || {}).find(k => k.toLowerCase().includes('roas'));
        if (roasKey && row.changes[roasKey] !== undefined && !isNaN(row.changes[roasKey])) {
            isRoasDown = row.changes[roasKey] < 0;
        }
        else {
            const currRoasKey = Object.keys(row.current || {}).find(k => k.toLowerCase().includes('roas'));
            if (currRoasKey && row.current?.[currRoasKey] !== undefined && row.compared?.[currRoasKey] !== undefined && row.compared?.[currRoasKey] !== null) {
                const curr = Number(row.current[currRoasKey] || 0);
                const prev = Number(row.compared[currRoasKey] || 0);
                if (prev > 0 && curr < prev) {
                    isRoasDown = true;
                }
            }
        }
    }
    let isGood = change >= 0;
    if (isCpocOrCpa) {
        // CPOC / CPA / CPC: DOWN is good (green), UP is bad (red)
        isGood = change <= 0;
    }
    else if (isSpendOrCost && isRoasDown) {
        isGood = change < 0;
    }
    const badgeColor = isGood ? '#10B981' : '#EF4444';
    const badgeBg = isGood ? '#ECFDF5' : '#FEF2F2';
    return (_jsxs(Box, { className: "divergence-table-metric", sx: { display: 'flex', flexDirection: 'column', minWidth: '120px' }, children: [_jsxs(Box, { sx: { display: 'inline-flex', alignItems: 'center', gap: '6px' }, children: [_jsx(StyledCurrency, { value: formatMetricValue(current, metricKey), className: isSummary ? "all-Font-Sizes-16" : "all-Font-Sizes", fontSize: "14px", symbolSize: "14px", valueColor: "#181D27", symbolColor: "#9CA3AF", valueWeight: isSummary ? 600 : 400, symbolWeight: isSummary ? 600 : 400, style: { lineHeight: 1.5, verticalAlign: 'middle' } }), isChangeValid && (_jsxs(Box, { className: "divergence-change-badge", sx: {
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '3px',
                            color: badgeColor,
                            bgcolor: badgeBg,
                            px: 1,
                            py: '2px',
                            borderRadius: 2,
                            fontWeight: 600,
                            flexShrink: 0,
                            verticalAlign: 'middle',
                        }, children: [Math.abs(change).toFixed(0), "%", change >= 0
                                ? _jsx(TrendingUpIcon, { sx: { fontSize: 12 } })
                                : _jsx(TrendingDownIcon, { sx: { fontSize: 12 } })] }))] }), hasCompared && (_jsx(Box, { sx: { mt: '2px' }, children: _jsx(StyledCurrency, { value: formatMetricValue(compared, metricKey), className: "all-Font-Sizes-12", fontSize: "13px", symbolSize: "13px", valueColor: "#74777d", symbolColor: "#9CA3AF", valueWeight: 400, symbolWeight: 400, style: { lineHeight: 1.5 } }) }))] }));
};
const RevenueHeaderWithTooltip = ({ label, formula }) => {
    const cleaned = (formula || "").replace(/\s+/g, "").toUpperCase();
    const prettyFormula = cleaned ? cleaned.replace(/([+-])/g, " $1 ") : "G - D + S + T";
    const varMap = {
        G: "Gross Sales",
        D: "Discounts",
        S: "Shipping",
        T: "Taxes",
    };
    const usedVars = Array.from(new Set((cleaned.match(/[A-Z]/g) || []))).filter(v => varMap[v]);
    return (_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 0.5 }, children: [_jsx("span", { children: label }), _jsx(InfoTooltip, { title: _jsxs(Box, { sx: { p: 0.5 }, children: ["Derived using: (", formula, ")"] }), placement: "top", size: 14, iconColor: "#9CA3AF" })] }));
};
const ConvRateHeaderWithTooltip = ({ label }) => {
    return (_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 0.5 }, children: [_jsx("span", { children: label }), _jsx(InfoTooltip, { title: _jsx(Box, { sx: { p: 0.5 }, children: _jsx(Typography, { sx: { fontSize: 8, fontWeight: 500 }, children: "((order/session)x100)" }) }), placement: "top", size: 14, iconColor: "#9CA3AF" })] }));
};
const BounceRateHeaderWithTooltip = ({ label }) => {
    return (_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 0.5 }, children: [_jsx("span", { children: label }), _jsx(InfoTooltip, { title: _jsx(Box, { sx: { p: 0.5 }, children: _jsx(Typography, { sx: { fontSize: 7, fontWeight: 500 }, children: "(((sessions-engaged sessions)/sessions)x100)" }) }), placement: "top", size: 14, iconColor: "#9CA3AF" })] }));
};
// ✅ Helper functions
const getHiddenColumnsFromPreferences = (preferences, networkKey) => {
    const hiddenSet = new Set();
    if (!preferences?.[networkKey])
        return hiddenSet;
    preferences[networkKey].forEach(item => {
        const metricKey = Object.keys(item)[0];
        const config = item[metricKey];
        if (config?.visibility === false) {
            hiddenSet.add(metricKey);
        }
    });
    return hiddenSet;
};
const getColumnOrderFromPreferences = (preferences, networkKey, defaultMetrics) => {
    if (!preferences?.[networkKey] || preferences[networkKey].length === 0) {
        return defaultMetrics;
    }
    const orderedMetrics = [];
    const preferencesKeys = [];
    preferences[networkKey].forEach(item => {
        const metricKey = Object.keys(item)[0];
        const config = item[metricKey];
        preferencesKeys.push(metricKey);
        if (config?.visibility !== false) {
            orderedMetrics.push({ key: metricKey, order: config?.order ?? 999 });
        }
    });
    orderedMetrics.sort((a, b) => a.order - b.order);
    const sortedKeys = orderedMetrics.map(m => m.key);
    const remainingMetrics = defaultMetrics.filter(m => !preferencesKeys.includes(m));
    return [...sortedKeys, ...remainingMetrics];
};
const getNetworkKeyFromTitle = (title) => {
    const t = title.toLowerCase();
    if (t.includes('shopify'))
        return 'shopify';
    if (t.includes('analytics') && !t.includes('acquisition'))
        return 'ga';
    if (t.includes('acquisition'))
        return 'ga_acquisition';
    if (t.includes('meta'))
        return 'meta';
    if (t.includes('adword') || t.includes('google ads'))
        return 'adword';
    if (t.includes('bing'))
        return 'bing';
    if (t.includes('criteo'))
        return 'criteo';
    if (t.includes('flow'))
        return 'klaviyo_flow';
    if (t.includes('email'))
        return 'klaviyo_email';
    if (t.includes('sms'))
        return 'klaviyo_sms';
    return title.toLowerCase().replace(/\s+/g, '_');
};
const DivergenceTable = ({ reportData, loading, showGACompare, setShowGACompare }) => {
    const dispatch = useDispatch();
    const preferences = useSelector(selectPreferences);
    const [collapsedChannels, setCollapsedChannels] = React.useState({});
    const [columnVisibilityAnchor, setColumnVisibilityAnchor] = React.useState({});
    // ✅ Preferences are now fetched by the parent page once on mount
    const hasShopify = React.useMemo(() => {
        if (!Array.isArray(reportData))
            return false;
        return reportData.some((r) => r.status === 'fulfilled' && r.value.network === 'shopify');
    }, [reportData]);
    const hasGa = React.useMemo(() => {
        if (!Array.isArray(reportData))
            return false;
        return reportData.some((r) => r.status === 'fulfilled' && r.value.network === 'ga');
    }, [reportData]);
    const hasAdword = React.useMemo(() => {
        if (!Array.isArray(reportData))
            return false;
        return reportData.some((r) => r.status === 'fulfilled' && (r.value.network === 'adword' || r.value.network === 'google ads' || r.value.network === 'googleads'));
    }, [reportData]);
    const hasMeta = React.useMemo(() => {
        if (!Array.isArray(reportData))
            return false;
        return reportData.some((r) => r.status === 'fulfilled' && (r.value.network === 'meta' || r.value.network === 'facebook'));
    }, [reportData]);
    const shopifyFormula = React.useMemo(() => {
        if (!Array.isArray(reportData))
            return undefined;
        const shopify = reportData.find((r) => r.status === "fulfilled" && r.value?.network === "shopify");
        return shopify?.value?.formula;
    }, [reportData]);
    const handleColumnVisibilityChange = useCallback((networkKey, newHiddenColumns, allMetrics) => {
        const existingPrefs = preferences?.[networkKey] || [];
        const existingOrderMap = new Map();
        existingPrefs.forEach((item, index) => {
            const key = Object.keys(item)[0];
            existingOrderMap.set(key, item[key]?.order ?? index + 1);
        });
        const newPreferences = [];
        allMetrics.forEach((metric, index) => {
            const isHidden = newHiddenColumns.has(metric);
            const existingOrder = existingOrderMap.get(metric) ?? index + 1;
            newPreferences.push({
                [metric]: {
                    visibility: !isHidden,
                    order: existingOrder
                }
            });
        });
        dispatch(updateNetworkPreference({ networkKey, metrics: newPreferences }));
        const updatedPreferences = {
            ...preferences,
            [networkKey]: newPreferences
        };
        dispatch(savePreferences({ preferences: updatedPreferences }));
    }, [dispatch, preferences]);
    const formatFullDate = (str) => {
        if (!str)
            return "";
        if (str === "Summary" || str === "Total Summary")
            return str;
        const parts = str.split(" - ");
        if (parts.length === 2) {
            const d1 = moment(parts[0].trim(), ["YYYY-MM-DD", "DD MMM", "MMM DD", "DD MMM YYYY", "MMM DD, YYYY"]);
            const d2 = moment(parts[1].trim(), ["YYYY-MM-DD", "DD MMM", "MMM DD", "DD MMM YYYY", "MMM DD, YYYY"]);
            if (d1.isValid() && d2.isValid()) {
                return `${d1.format('MM/DD/YYYY')} - ${d2.format('MM/DD/YYYY')}`;
            }
        }
        else {
            const d = moment(str.trim(), ["YYYY-MM-DD", "DD MMM YYYY", "MMM DD, YYYY"], true);
            if (d.isValid())
                return d.format('MM/DD/YYYY');
        }
        return str;
    };
    const handleColumnOrderChange = useCallback((networkKey, newOrder, allMetrics) => {
        const existingPrefs = preferences?.[networkKey] || [];
        const existingVisibilityMap = new Map();
        existingPrefs.forEach(item => {
            const key = Object.keys(item)[0];
            existingVisibilityMap.set(key, item[key]?.visibility ?? true);
        });
        const newPreferences = [];
        newOrder.forEach((metric, index) => {
            if (metric === 'date')
                return;
            const isVisible = existingVisibilityMap.get(metric) ?? true;
            newPreferences.push({
                [metric]: { visibility: isVisible, order: index + 1 }
            });
        });
        allMetrics.forEach((metric, index) => {
            if (!newOrder.includes(metric)) {
                const isVisible = existingVisibilityMap.get(metric) ?? true;
                newPreferences.push({
                    [metric]: { visibility: isVisible, order: newOrder.length + index + 1 }
                });
            }
        });
        dispatch(updateNetworkPreference({ networkKey, metrics: newPreferences }));
        const updatedPreferences = {
            ...preferences,
            [networkKey]: newPreferences
        };
        dispatch(savePreferences({ preferences: updatedPreferences }));
    }, [dispatch, preferences]);
    const handleColumnVisibilityOpen = useCallback((sectionKey, anchorEl) => {
        setColumnVisibilityAnchor(prev => ({ ...prev, [sectionKey]: anchorEl }));
    }, []);
    const handleColumnVisibilityClose = useCallback((sectionKey) => {
        setColumnVisibilityAnchor(prev => ({ ...prev, [sectionKey]: null }));
    }, []);
    if (!Array.isArray(reportData))
        return null;
    const sections = [];
    // Find this section in the code where sections are created:
    reportData
        .filter((r) => r.status === 'fulfilled')
        .forEach((r) => {
        const network = r.value.network;
        const data = r.value.data;
        if (!data)
            return;
        if (network === "ga") {
            // ✅ Show even if data is empty
            if (data.main)
                sections.push({ title: "Google Analytics", block: data.main });
            if (data.acquisition)
                sections.push({ title: "GA Acquisition", block: data.acquisition, isAcquisition: true });
        }
        else if (network === "klaviyo") {
            // ✅ Show even if data is empty
            if (data.email)
                sections.push({ title: "Email Campaigns", block: data.email });
            if (data.sms)
                sections.push({ title: "Sms", block: data.sms });
            if (data.flow)
                sections.push({ title: "Email Flows", block: data.flow });
        }
        else {
            // ✅ Show even if data is empty
            if (data.current || data.compared)
                sections.push({ title: network.toUpperCase(), block: data, network: network });
        }
    });
    // ✅ CORRECT - Explicit ordering: Shopify → GA → Others → GA Acquisition
    const shopifySections = sections.filter(s => s.title.toLowerCase().includes('shopify'));
    const gaSections = sections.filter(s => s.title.toLowerCase().includes('analytics') && !s.isAcquisition);
    const acquisitionSections = sections.filter(s => s.isAcquisition);
    const otherSections = sections.filter(s => !s.title.toLowerCase().includes('shopify') &&
        !s.title.toLowerCase().includes('analytics') &&
        !s.isAcquisition);
    // ✅ ORDER: Shopify → GA Main → Other Networks → GA Acquisition
    const orderedSections = [
        ...shopifySections,
        ...gaSections,
        ...otherSections,
        ...acquisitionSections
    ];
    const shouldShowGA = !hasShopify || showGACompare;
    return (_jsxs(Box, { display: "flex", flexDirection: "column", gap: 1, sx: {
            // ✅ Keep scrollbar hidden
            '& .MuiTableContainer-root::-webkit-scrollbar': {
                display: 'none',
            },
            '& .MuiTableContainer-root': {
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
            }
        }, children: [hasShopify && hasGa && (_jsx(Box, { sx: { display: 'flex', justifyContent: 'flex-start', px: 1, py: 0.5 }, children: _jsx(Box, { className: "compare-toggle-container", children: _jsx(FormControlLabel, { control: _jsx(Switch, { checked: showGACompare, onChange: (e) => setShowGACompare(e.target.checked), className: "compare-switch" }), label: "Do you want to compare revenue with Google Analytics?", className: "compare-label all-Font-Sizes", sx: { '.MuiTypography-root': { fontWeight: 500, fontSize: '13px !important' } } }) }) })), loading && orderedSections.length === 0 && (_jsx(Box, { sx: { height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }, children: _jsx(CommonLoader, { className: 'table-loader' }) })), orderedSections
                .filter(s => s.title !== "Google Analytics" || shouldShowGA)
                .map((section, idx) => {
                const { block, title, isAcquisition, network } = section;
                const isShopify = network === 'shopify' || title.toLowerCase() === 'shopify';
                const isGa = network === 'ga' || title.toLowerCase() === 'google analytics';
                let metrics = block.metrics || [];
                if (isShopify || isGa) {
                    if (!hasAdword)
                        metrics = metrics.filter((m) => m !== 'google_cost');
                    if (!hasMeta)
                        metrics = metrics.filter((m) => m !== 'meta_cost');
                    if (!hasAdword || !hasMeta)
                        metrics = metrics.filter((m) => m !== 'total_cost');
                }
                const currentData = block.current?.data || [];
                const comparedData = block.compared?.data || [];
                const networkKey = getNetworkKeyFromTitle(title);
                const sectionKey = `${title}-${idx}`;
                const hiddenColumns = getHiddenColumnsFromPreferences(preferences, networkKey);
                const columnOrder = getColumnOrderFromPreferences(preferences, networkKey, metrics);
                const tableRows = [];
                const currentMap = new Map();
                const comparedMap = new Map();
                currentData.forEach((row) => {
                    const key = isAcquisition
                        ? row.channel
                        : moment(row.date).format("YYYY-MM");
                    currentMap.set(key, row);
                });
                comparedData.forEach((row) => {
                    const key = isAcquisition
                        ? row.channel
                        : moment(row.date).format("YYYY-MM");
                    comparedMap.set(key, row);
                });
                const formatMetricLabel = (key) => {
                    const dictionary = {
                        gross_sales: "Gross Sales",
                        meta_cost: "Meta Cost",
                        google_cost: "Google Cost",
                        total_cost: "Total Cost",
                        outboundclicks: "Outbound Clicks",
                        avg_engagement_time_per_session: "Avg Eng Time",
                    };
                    if (dictionary[key.toLowerCase()]) {
                        return dictionary[key.toLowerCase()];
                    }
                    const removablePrefixes = ["ga"];
                    let pureKey = key;
                    const firstPart = key.split("_")[0];
                    if (removablePrefixes.includes(firstPart)) {
                        pureKey = key.replace(`${firstPart}_`, "");
                    }
                    const ACRONYMS = new Set(["aov", "ctr", "cpc", "cpa", "cpm", "roas", "octr", "cpoc"]);
                    return pureKey
                        .replace(/_/g, " ")
                        .replace(/([a-z])([A-Z])/g, "$1 $2")
                        .split(" ")
                        .map((word) => ACRONYMS.has(word.toLowerCase())
                        ? word.toUpperCase()
                        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                        .join(" ");
                };
                const currentLabels = block.current?.labels?.length ? block.current.labels : Array.from(currentMap.keys()).sort();
                const comparedLabels = block.compared?.labels?.length ? block.compared.labels : Array.from(comparedMap.keys()).sort();
                if (isAcquisition) {
                    const allKeys = Array.from(new Set([
                        ...currentMap.keys(),
                        ...comparedMap.keys()
                    ])).sort();
                    allKeys.forEach((key) => {
                        const currentRow = currentMap.get(key);
                        const comparedRow = comparedMap.get(key);
                        tableRows.push({
                            id: `header-${key}`,
                            date: key,
                            rawDate: key,
                            isChannelHeader: true,
                            isExpanded: !collapsedChannels[key],
                            current: {},
                            compared: undefined,
                            changes: {}
                        });
                        if (!collapsedChannels[key]) {
                            const dataRow = {
                                id: `data-${key}`,
                                date: block.current?.dateRange
                                    ? formatFullDate(block.current.dateRange)
                                    : "",
                                comparedDate: block.compared?.dateRange
                                    ? formatFullDate(block.compared.dateRange)
                                    : "",
                                hoverDate: block.current?.dateRange || "",
                                hoverComparedDate: block.compared?.dateRange || "",
                                rawDate: key,
                                current: {},
                                compared: comparedRow ? {} : undefined,
                                changes: {}
                            };
                            metrics.forEach((metric) => {
                                const currentVal = currentRow?.[metric];
                                const comparedVal = comparedRow?.[metric];
                                if (currentVal !== undefined) {
                                    dataRow.current[metric] = currentVal;
                                }
                                if (comparedVal !== undefined) {
                                    dataRow.compared = dataRow.compared || {};
                                    dataRow.compared[metric] = comparedVal;
                                }
                                if (currentVal !== undefined &&
                                    comparedVal !== undefined &&
                                    comparedVal !== 0) {
                                    dataRow.changes[metric] =
                                        ((currentVal - comparedVal) / Math.abs(comparedVal)) * 100;
                                }
                            });
                            tableRows.push(dataRow);
                        }
                    });
                }
                else {
                    const rowCount = Math.max(currentLabels.length, comparedLabels.length);
                    for (let i = 0; i < rowCount; i++) {
                        const currentKey = currentLabels[i];
                        const comparedKey = comparedLabels[i];
                        const currentRow = currentKey ? currentMap.get(currentKey) : undefined;
                        const comparedRow = comparedKey ? comparedMap.get(comparedKey) : undefined;
                        const displayKey = currentKey;
                        const row = {
                            date: displayKey ? moment(displayKey, "YYYY-MM").format("MMM YY") : "-",
                            rawDate: displayKey || `${i}`,
                            hoverDate: block.current?.dateRange || "",
                            hoverComparedDate: block.compared?.dateRange || "",
                            comparedDate: comparedKey ? moment(comparedKey, "YYYY-MM").format("MMM YY") : "",
                            current: {},
                            compared: undefined,
                            changes: {}
                        };
                        metrics.forEach((metric) => {
                            const currentVal = currentRow?.[metric];
                            const comparedVal = comparedRow?.[metric];
                            if (currentVal !== undefined) {
                                row.current[metric] = currentVal;
                            }
                            if (comparedVal !== undefined) {
                                if (!row.compared) {
                                    row.compared = {};
                                }
                                row.compared[metric] = comparedVal;
                            }
                            // ✅ Only calculate change if BOTH exist
                            if (currentVal !== undefined &&
                                comparedVal !== undefined &&
                                comparedVal !== 0) {
                                row.changes[metric] =
                                    ((currentVal - comparedVal) / Math.abs(comparedVal)) * 100;
                            }
                        });
                        tableRows.push(row);
                    }
                }
                if (block.summary?.current_totals) {
                    tableRows.push({
                        date: isAcquisition ? "Total Summary" : "Summary",
                        isSummary: true,
                        hoverDate: block.current?.dateRange || "",
                        hoverComparedDate: block.compared?.dateRange || "",
                        comparedDate: block.compared?.dateRange || "",
                        current: block.summary.current_totals,
                        compared: block.summary.compared_totals,
                        changes: block.summary.changes
                    });
                }
                // ✅ Build columns with ALL metrics (not filtered by visibility)
                const columns = [
                    {
                        id: "date",
                        label: isAcquisition ? "Month" : "Month",
                        width: isAcquisition ? "240px" : "180px",
                        sortable: false,
                        draggable: false
                    },
                    ...metrics.map((m) => {
                        const label = formatMetricLabel(m);
                        const isRevenueColumn = m.toLowerCase() === 'revenue';
                        const isConvRateColumn = m.toLowerCase() === 'conv_rate';
                        const isBounceRateColumn = m.toLowerCase() === 'bounce_rate';
                        return {
                            id: m,
                            label: isConvRateColumn
                                ? _jsx(ConvRateHeaderWithTooltip, { label: label })
                                : isBounceRateColumn
                                    ? _jsx(BounceRateHeaderWithTooltip, { label: label })
                                    : (isShopify && isRevenueColumn)
                                        ? _jsx(RevenueHeaderWithTooltip, { label: label, formula: shopifyFormula })
                                        : label,
                            width: "180px",
                            sortable: false,
                            draggable: true
                        };
                    })
                ];
                // ✅ ALL metrics for visibility panel (not filtered)
                const allMetricsForVisibility = metrics;
                const renderCell = (row, column) => {
                    if (row.isChannelHeader) {
                        if (column.id === "date") {
                            return (_jsxs(Box, { sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                }, onClick: () => {
                                    setCollapsedChannels(prev => ({
                                        ...prev,
                                        [row.rawDate]: !prev[row.rawDate]
                                    }));
                                }, children: [_jsx(KeyboardArrowDownIcon, { sx: {
                                            mr: 1,
                                            transform: row.isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                                            transition: 'transform 0.2s',
                                            fontSize: 20
                                        } }), _jsx(Typography, { fontWeight: 600, sx: { fontSize: '14px !important', whiteSpace: 'nowrap' }, children: row.date })] }));
                        }
                        return "";
                    }
                    if (column.id === "date") {
                        const currentDisplay = row.date;
                        const comparedDisplay = row.comparedDate ? row.comparedDate : (row.compared ? currentDisplay : '');
                        // ✅ Acquisition → no tooltip
                        if (isAcquisition) {
                            return (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', pl: 4 }, children: [_jsx(Typography, { className: "all-Font-Sizes", fontWeight: row.isSummary ? 700 : 400, sx: { fontSize: '14px !important', whiteSpace: 'nowrap' }, children: row.date }), row.compared && !row.isSummary && (_jsx(Typography, { className: "all-Font-Sizes-12", sx: { fontSize: '13px !important', color: '#74777d', whiteSpace: 'nowrap', mt: 0.5 }, children: comparedDisplay }))] }));
                        }
                        const hoverTooltipTitle = (row.hoverDate ? formatFullDate(row.hoverDate) : row.date) ||
                            "";
                        const comparedHoverTooltipTitle = (row.hoverComparedDate ? formatFullDate(row.hoverComparedDate) : row.comparedDate) ||
                            "";
                        const tooltipTitle = row.isSummary ? (_jsxs(Box, { children: [_jsx(Typography, { sx: { fontSize: 12 }, children: formatFullDate(row.hoverDate) }), _jsx(Typography, { sx: { fontSize: 12, color: '#9CA3AF', mt: 0.5 }, children: formatFullDate(row.hoverComparedDate) })] })) : hoverTooltipTitle;
                        return (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', pl: isAcquisition ? 4 : 0 }, children: [_jsx(HoverTooltip, { title: tooltipTitle, placement: "top-start", children: _jsx(Typography, { className: "all-Font-Sizes", fontWeight: row.isSummary ? 700 : 400, sx: {
                                            fontSize: '14px !important',
                                            whiteSpace: 'nowrap',
                                            cursor: 'pointer',
                                            width: 'fit-content'
                                        }, children: row.date }) }), row.compared && !row.isSummary && (_jsx(HoverTooltip, { title: comparedHoverTooltipTitle, placement: "bottom-start", children: _jsx(Typography, { className: "all-Font-Sizes-12", sx: { fontSize: '13px !important', color: '#74777d', whiteSpace: 'nowrap', mt: 0.5, cursor: 'pointer', width: 'fit-content' }, children: comparedDisplay }) }))] }));
                    }
                    return (_jsx(MetricCell, { current: row.current?.[column.id], compared: row.compared?.[column.id], change: row.changes?.[column.id], metricKey: column.id, isSummary: row.isSummary, row: row }));
                };
                const getNetworkIcon = (titleStr) => {
                    const t = titleStr.toLowerCase();
                    if (t.includes('shopify'))
                        return NETWORK_ICON_MAP.shopify;
                    if (t.includes('analytics') || t.includes('ga') || t.includes('acquisition'))
                        return NETWORK_ICON_MAP.ga;
                    if (t.includes('bing'))
                        return NETWORK_ICON_MAP.bing;
                    if (t.includes('meta'))
                        return NETWORK_ICON_MAP.meta;
                    if (t.includes('adword') || t.includes('google ads'))
                        return NETWORK_ICON_MAP.adword;
                    if (t.includes('email'))
                        return '/assets/gmail.svg';
                    if (t.includes('flow'))
                        return '/assets/flow.svg';
                    if (t.includes('sms'))
                        return '/assets/sms.svg';
                    if (t.includes('klaviyo'))
                        return NETWORK_ICON_MAP.klaviyo;
                    if (t.includes('criteo'))
                        return NETWORK_ICON_MAP.criteo;
                    return null;
                };
                const iconPath = getNetworkIcon(title);
                const formatTitle = (text) => {
                    if (!text)
                        return '';
                    // Keep these titles as-is (no transformation)
                    const preserveTitles = ['GA Acquisition', 'Google Analytics', 'Email Campaigns', 'Sms', 'Email Flows'];
                    if (preserveTitles.includes(text))
                        return text;
                    // For other titles, capitalize first letter of each word
                    return text
                        .split(' ')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                        .join(' ');
                };
                return (_jsxs(Accordion, { expanded: true, onChange: () => { }, className: "divergence-accordion", sx: { m: 0, border: '1px solid #D0D5DD', borderRadius: '8px' }, children: [_jsxs(AccordionSummary, { onClick: (e) => e.stopPropagation(), sx: {
                                flexDirection: 'row-reverse',
                                // ✅ HEIGHT FIX
                                minHeight: "45px",
                                '&.Mui-expanded': {
                                    minHeight: "45px",
                                },
                                // ✅ REMOVE EXTRA CONTENT MARGIN (IMPORTANT)
                                '& .MuiAccordionSummary-content': {
                                    margin: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                },
                                '& .MuiAccordionSummary-content.Mui-expanded': {
                                    margin: 0,
                                },
                                cursor: 'default !important',
                                '&.MuiButtonBase-root': {
                                    cursor: 'default !important',
                                },
                                '&:hover': {
                                    cursor: 'default !important',
                                },
                                '& .MuiAccordionSummary-expandIconWrapper': {
                                    display: 'none',
                                },
                            }, className: "all-Font-Sizes-16 divergence-accordion", children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: "8px" }, children: [iconPath && (_jsx("img", { src: iconPath, alt: title, style: { width: 24, height: 24, objectFit: 'contain' } })), _jsx(Typography, { fontWeight: 600, className: "divergence-accordion all-Font-Sizes", children: formatTitle(title) })] }), _jsx(Box, { onClick: (e) => e.stopPropagation(), onFocus: (e) => e.stopPropagation(), className: "divergence-table-toolbar small-icon", children: _jsx(TableToolbar, { showSearch: false, enableColumnFilters: false, showFullScreen: true, showExpand: false, showRows: false, title: "", enableColumnVisibility: true, onColumnVisibilityClick: (e) => {
                                            handleColumnVisibilityOpen(sectionKey, e.currentTarget);
                                        } }) })] }), _jsx(AccordionDetails, { sx: {
                                p: 0,
                                '& .MuiTable-root .MuiTableRow-root [data-column-id="date"]': {
                                    borderRight: '1px solid #e2e8f0',
                                },
                                '& .reusable-table-header-cell': {
                                    padding: '8px 8px 8px 5px !important',
                                },
                                '& .divergence-change-badge': {
                                    fontSize: '12px !important',
                                },
                            }, className: "divergence-table", children: _jsx(CommonTable, { isLoading: loading, columns: columns, rows: tableRows, renderCell: renderCell, pinnedColumns: { left: ['date'], right: [] }, stickyHeader: true, maxHeight: "500px", expandTrigger: "none", 
                                // ✅ Column visibility from Redux
                                hiddenColumns: hiddenColumns, onColumnVisibilityChange: (newHidden) => {
                                    handleColumnVisibilityChange(networkKey, newHidden, metrics);
                                }, columnVisibilityAnchorEl: columnVisibilityAnchor[sectionKey] || null, onColumnVisibilityClose: () => handleColumnVisibilityClose(sectionKey), 
                                // ✅ FIX: Pass ALL metrics for visibility panel
                                columnVisibilityConfig: {
                                    enable: true,
                                    columns: allMetricsForVisibility, // ✅ ALL metrics (not filtered)
                                    enableReorder: true,
                                    showHiddenColumns: true // ✅ Add this if CommonTable supports it
                                }, 
                                // ✅ Column reorder from Redux
                                enableColumnReorder: true, columnOrder: columnOrder, onColumnOrderChange: (newOrder) => {
                                    handleColumnOrderChange(networkKey, newOrder, metrics);
                                }, rowSx: (row) => {
                                    const base = {
                                        "& .MuiTableCell-root": {
                                            padding: "8px !important", // ✅ yahi se
                                        },
                                    };
                                    if (row.isChannelHeader) {
                                        return {
                                            ...base,
                                            "& .MuiTableCell-root": {
                                                ...base["& .MuiTableCell-root"],
                                                backgroundColor: "#fffcf5 !important",
                                            },
                                        };
                                    }
                                    if (row.isSummary) {
                                        return {
                                            ...base,
                                            "& .MuiTableCell-root": {
                                                ...base["& .MuiTableCell-root"],
                                                position: "sticky",
                                                bottom: 0,
                                                zIndex: 4,
                                                backgroundColor: "#F4F4F5 !important",
                                                borderTop: "1px solid #e2e8f0",
                                            },
                                            '& [data-column-id="date"]': {
                                                zIndex: 5,
                                                left: 0,
                                            },
                                        };
                                    }
                                    return base;
                                } }) })] }, idx));
            })] }));
};
export default DivergenceTable;
